import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { getLogger } from '@confluence/logger';
import { extractContentFeaturesSkipExpand } from '@confluence/read-time/entry-points/extractContentFeaturesSkipExpand.ts';

import {
	getCompanionTipScrollDepthY,
	getCompanionTipScrollMetrics,
} from './companionTipScrollMetrics.ts';

const COMPANION_TIP_SCROLL_DEPTH_THRESHOLD = 0.8;
const COMPANION_TIP_MIN_BODY_CHARACTER_COUNT = 500;
const logger = getLogger('ViewPage');

export type CompanionTipContentBodyDynamic =
	| { representation?: string | null; value?: string | null }
	| null
	| undefined;
export type CompanionTipCurrentContentBodyDynamic = {
	contentId: string;
	contentBodyDynamic: CompanionTipContentBodyDynamic;
} | null;

type RecordCompanionTipScrollSignal = (options: {
	variables: {
		cloudId: string;
		pageId: string;
	};
}) => Promise<unknown>;

const companionTipBodyHasTextOverCharacterCount = (
	contentBodyDynamic: CompanionTipContentBodyDynamic,
) => {
	if (contentBodyDynamic?.representation !== 'atlas_doc_format' || !contentBodyDynamic.value) {
		return false;
	}

	try {
		const { textLength } = extractContentFeaturesSkipExpand(JSON.parse(contentBodyDynamic.value));

		return textLength > COMPANION_TIP_MIN_BODY_CHARACTER_COUNT;
	} catch {
		return false;
	}
};

export const useCompanionTipScrollSignal = ({
	cloudId,
	contentId,
	companionTipContentBodyDynamicRef,
	recordCompanionTipScrollSignal,
}: {
	cloudId?: string | null;
	contentId?: string | null;
	companionTipContentBodyDynamicRef: {
		current: CompanionTipCurrentContentBodyDynamic;
	};
	recordCompanionTipScrollSignal: RecordCompanionTipScrollSignal;
}) => {
	const companionTipScrollSignalFiredContentIdRef = useRef<string | null>(null);
	const companionTipUserScrollIntentContentIdRef = useRef<string | null>(null);
	const recordCompanionTipScrollSignalRef = useRef(recordCompanionTipScrollSignal);

	useEffect(() => {
		recordCompanionTipScrollSignalRef.current = recordCompanionTipScrollSignal;
	}, [recordCompanionTipScrollSignal]);

	useEffect(() => {
		const maybeRecordCompanionTipScrollSignal = (companionTipScrollDepthY: number) => {
			// Gate the signal on scroll depth, identifiers, once-per-content, user intent, and experiment enrollment.
			const hasCompanionTipScrollSignalPrerequisites =
				companionTipScrollDepthY >= COMPANION_TIP_SCROLL_DEPTH_THRESHOLD &&
				Boolean(cloudId) &&
				Boolean(contentId) &&
				companionTipScrollSignalFiredContentIdRef.current !== contentId &&
				companionTipUserScrollIntentContentIdRef.current === contentId &&
				expVal('cc_companion_tip_emails_api_exp', 'isEnabled', false);

			const companionTipContentBodyDynamicState = companionTipContentBodyDynamicRef.current;

			// Only trust body.dynamic when it belongs to the currently viewed content.
			const companionTipCurrentContentBodyDynamic =
				companionTipContentBodyDynamicState !== null &&
				companionTipContentBodyDynamicState.contentId === contentId
					? companionTipContentBodyDynamicState.contentBodyDynamic
					: undefined;

			// Before the tracker callback runs, body.dynamic is unknown, so let the backend decide.
			const hasNoCompanionTipBodyDynamicTrackerState = companionTipContentBodyDynamicState === null;
			// During SPA transitions, stale body data belongs to a previous contentId.
			const hasStaleCompanionTipBodyDynamicForDifferentContent =
				companionTipContentBodyDynamicState !== null &&
				companionTipContentBodyDynamicState.contentId !== contentId;
			// Fast live-page rendering can confirm the current content before body.dynamic is available.
			const hasUnavailableCompanionTipBodyDynamicForCurrentContent =
				companionTipContentBodyDynamicState !== null &&
				companionTipContentBodyDynamicState.contentId === contentId &&
				companionTipCurrentContentBodyDynamic === undefined;

			const shouldUseBackendCompanionTipBodyLengthFilter =
				hasNoCompanionTipBodyDynamicTrackerState ||
				hasStaleCompanionTipBodyDynamicForDifferentContent ||
				hasUnavailableCompanionTipBodyDynamicForCurrentContent;

			// Fire only when local ADF length passes or the backend is deliberately left to decide.
			const companionTipBodyHasRequiredLength =
				hasCompanionTipScrollSignalPrerequisites &&
				(shouldUseBackendCompanionTipBodyLengthFilter ||
					companionTipBodyHasTextOverCharacterCount(companionTipCurrentContentBodyDynamic));

			// Mark before firing so retries/rejections cannot duplicate the signal for this content.
			if (
				hasCompanionTipScrollSignalPrerequisites &&
				companionTipBodyHasRequiredLength &&
				cloudId &&
				contentId
			) {
				companionTipScrollSignalFiredContentIdRef.current = contentId;
				void recordCompanionTipScrollSignalRef
					.current({
						variables: {
							cloudId,
							pageId: contentId,
						},
					})
					.catch(() => {
						logger.warn`Failed to record companion tip scroll signal`;
					});
			}
		};

		const handleScroll = (event?: Event) => {
			const { scrollY, innerHeight } = window;
			const { scrollHeight } = document.documentElement;

			maybeRecordCompanionTipScrollSignal(
				getCompanionTipScrollDepthY(
					getCompanionTipScrollMetrics({
						scrollTarget: event?.target ?? null,
						fallbackScrollContainer: document.getElementById('AkMainContent'),
						fallbackScrollTop: scrollY,
						fallbackClientHeight: innerHeight,
						fallbackScrollHeight: scrollHeight,
					}),
				),
			);
		};

		const debounceScroll = debounce(handleScroll, 250 /*ms*/);
		const setCompanionTipUserScrollIntent = () => {
			companionTipUserScrollIntentContentIdRef.current = contentId ?? null;
		};

		// Treat keyboard navigation keys as explicit scroll intent for non-pointer users.
		const handleCompanionTipKeyboardScrollIntent = ({ key }: KeyboardEvent) => {
			if (
				key === 'ArrowDown' ||
				key === 'ArrowUp' ||
				key === 'PageDown' ||
				key === 'PageUp' ||
				key === 'Home' ||
				key === 'End' ||
				key === ' '
			) {
				setCompanionTipUserScrollIntent();
			}
		};

		if (expVal('cc_companion_tip_emails_api_exp', 'isEnabled', false)) {
			document.addEventListener('scroll', debounceScroll, true);
			document.addEventListener('wheel', setCompanionTipUserScrollIntent, {
				passive: true,
				capture: true,
			});
			document.addEventListener('touchmove', setCompanionTipUserScrollIntent, {
				passive: true,
				capture: true,
			});
			document.addEventListener('keydown', handleCompanionTipKeyboardScrollIntent);
		}

		return () => {
			document.removeEventListener('scroll', debounceScroll, true);
			document.removeEventListener('wheel', setCompanionTipUserScrollIntent, { capture: true });
			document.removeEventListener('touchmove', setCompanionTipUserScrollIntent, {
				capture: true,
			});
			document.removeEventListener('keydown', handleCompanionTipKeyboardScrollIntent);
			debounceScroll.cancel();
		};
	}, [cloudId, contentId, companionTipContentBodyDynamicRef]);
};
